import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Fab,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { collection, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../Configuration';
import { useFirebaseData } from '../contexts/FirebaseDataContext';
import { isAdmin } from '../utils/permissions';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';

const TeachersList = () => {
  const navigate = useNavigate();
  const {
    courses,
    instructors,
    refreshCollection,
    initialLoadComplete,
    loadingStates,
  } = useFirebaseData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const usageByInstructorId = useMemo(() => {
    const map = {};
    (courses || []).forEach((c) => {
      if (c.instructorId) {
        map[c.instructorId] = (map[c.instructorId] || 0) + 1;
      }
    });
    return map;
  }, [courses]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const openAdd = () => {
    setEditing(null);
    setName('');
    setOpenDialog(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setName(row.name || '');
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setEditing(null);
    setName('');
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      showSnackbar('Name is required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        await updateDoc(doc(db, 'Instructors', editing.id), {
          name: trimmed,
          updatedAt: new Date(),
        });
        showSnackbar('Teacher updated', 'success');
      } else {
        await addDoc(collection(db, 'Instructors'), {
          name: trimmed,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        showSnackbar('Teacher added', 'success');
      }
      closeDialog();
      await refreshCollection('instructors');
    } catch (e) {
      console.error(e);
      showSnackbar('Save failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    const n = usageByInstructorId[row.id] || 0;
    if (n > 0) {
      showSnackbar(
        `Cannot delete: ${n} subject(s) still use this teacher. Reassign them in Subjects first.`,
        'error'
      );
      return;
    }
    if (!window.confirm(`Delete teacher "${row.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'Instructors', row.id));
      showSnackbar('Teacher deleted', 'success');
      await refreshCollection('instructors');
    } catch (e) {
      console.error(e);
      showSnackbar('Delete failed', 'error');
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    {
      field: 'usage',
      headerName: 'Subjects linked',
      width: 140,
      sortable: false,
      valueGetter: (_, row) => usageByInstructorId[row.id] || 0,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton size="small" color="primary" onClick={() => openEdit(params.row)} aria-label="edit">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row)} aria-label="delete">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  if (!isAdmin()) {
    return null;
  }

  const gridLoading = !initialLoadComplete || loadingStates.instructors;

  return (
    <Box sx={{ p: 3, background: '#f8f9fa', minHeight: '100vh' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#2E7D32', mb: 1 }}>
        Teachers
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
        Add and manage instructors. Link them to subjects on the Subjects screen.
      </Typography>

      <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
        {gridLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={instructors}
            columns={columns}
            getRowId={(r) => r.id}
            slots={{ toolbar: GridToolbar }}
            autoHeight
            sx={{ minHeight: 400, border: 'none' }}
          />
        )}
      </Paper>

      <Fab
        color="primary"
        aria-label="add teacher"
        onClick={openAdd}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: '#4CAF50',
          '&:hover': { bgcolor: '#2E7D32' },
        }}
      >
        <AddIcon />
      </Fab>

      <Dialog open={openDialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit teacher' : 'Add teacher'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Full name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={submitting} color="primary">
            {submitting ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeachersList;
